// src/pages/admin/AdminMeetings.jsx
// ============================================================
// FEATURES:
//   1. Recurrence selector: Once / Mon–Fri / Mon–Sat / Custom days
//   2. Date range for recurring meetings
//   3. Cannot schedule meetings for past dates
//   4. Meet link activates 10 mins before meeting
//   5. Meet link expires after meeting time passes
// ============================================================

import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

import { useMeeting } from "../../context/MeetingContext";
import { useNotifications } from "../../context/NotificationContext";
import { useDepartments } from "../../hooks/useFirestore";
import {
  getTodayStr,
  getTodaysMeetings,
  getMeetingState,
  isEmployeeVisible,
  add30Minutes,
  getDurationMins,
  formatTimeStr,
} from "../../utils/meetings";

import {
  collection,
  addDoc,
  serverTimestamp,
  writeBatch,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

import { db } from "../../firebase/config";

import { ADMIN_NAV } from "../../utils/navigation";

import Layout from "../../components/common/Layout";

const DAY_LABELS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function getAllowedDays(recurrenceType, customDays) {
  if (recurrenceType === "mon-fri")
    return [1, 2, 3, 4, 5];

  if (recurrenceType === "mon-sat")
    return [1, 2, 3, 4, 5, 6];

  if (recurrenceType === "custom")
    return customDays;

  return [];
}

function getDatesBetween(
  startStr,
  endStr,
  allowedDays
) {
  const dates = [];

  const cursor = new Date(
    startStr + "T00:00:00"
  );

  const end = new Date(endStr + "T00:00:00");

  const allowed = new Set(allowedDays);

  while (cursor <= end) {
    if (allowed.has(cursor.getDay())) {
      dates.push(
        `${cursor.getFullYear()}-${String(
          cursor.getMonth() + 1
        ).padStart(2, "0")}-${String(
          cursor.getDate()
        ).padStart(2, "0")}`
      );
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}


// ─────────────────────────────────────────────────────────────
// AdminMeetings Component
// ─────────────────────────────────────────────────────────────
export function AdminMeetings() {
  const { userProfile } = useAuth();

  const { allTodayMeetings, todayMeetingsLoading, joinMeeting, leaveMeeting } =
    useMeeting();
  const { addNotification } = useNotifications();

  const today = getTodayStr();

  const { departments } = useDepartments();
  const activeDepts = departments && departments.length > 0
    ? departments.filter((d) => d.active).map((d) => d.name)
    : ["Engineering", "Design", "QA", "HR", "Finance", "Operations", "Administration"];

  const [form, setForm] = useState({
    title: "",
    startDate: "",
    endDate: "",
    time: "",
    endTime: "",
    meetLink: "",
    audience: "all",
    agenda: "",
    recurrenceType: "once",
    customDays: [],
  });

  const [hasManuallyEditedEndTime, setHasManuallyEditedEndTime] = useState(false);

  const [saving, setSaving] = useState(false);

  const [msg, setMsg] = useState("");

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");
  const [deleting, setDeleting] = useState({});

  const [joining, setJoining] = useState({});

  const [listTab, setListTab] = useState("today");

  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(timer);
  }, []);

  // ─────────────────────────────────────────────────────────
  // Form Handlers
  // ─────────────────────────────────────────────────────────
  function handleChange(e) {
    setForm((p) => ({
      ...p,
      [e.target.name]: e.target.value,
    }));
  }

  function toggleCustomDay(day) {
    setForm((p) => ({
      ...p,

      customDays: p.customDays.includes(day)
        ? p.customDays.filter((d) => d !== day)
        : [...p.customDays, day],
    }));
  }

  // Preview recurring count
  const previewCount = useMemo(() => {
    if (form.recurrenceType === "once") {
      return form.startDate ? 1 : 0;
    }

    if (!form.startDate || !form.endDate) {
      return 0;
    }

    return getDatesBetween(
      form.startDate,
      form.endDate,
      getAllowedDays(
        form.recurrenceType,
        form.customDays
      )
    ).length;
  }, [
    form.recurrenceType,
    form.startDate,
    form.endDate,
    form.customDays,
  ]);

  // ─────────────────────────────────────────────────────────
  // Schedule Meeting
  // ─────────────────────────────────────────────────────────
  async function handleSchedule(e) {
    e.preventDefault();

    if (!userProfile?.uid) {
      setMsg(
        "❌ Profile still loading. Please wait."
      );

      return;
    }

    if (!form.time) {
      setMsg("❌ Start time is required.");
      return;
    }

    if (!form.endTime) {
      setMsg("❌ End time is required.");
      return;
    }

    // No past dates
    if (form.startDate < today) {
      setMsg(
        "❌ Start date cannot be in the past."
      );

      return;
    }

    // Past meeting time validation for today
    const now = new Date();
    const curTimeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    if (form.startDate === today && form.time < curTimeStr) {
      setMsg("❌ Start time cannot be in the past.");
      return;
    }

    if (!form.meetLink?.trim()) {
      setMsg("❌ Meeting link is required.");
      return;
    }
    if (!form.agenda?.trim()) {
      setMsg("❌ Agenda is required.");
      return;
    }

    // End Time must be after Start Time
    if (form.endTime <= form.time) {
      setMsg("❌ End time must be after start time.");
      return;
    }

    if (
      form.recurrenceType !== "once" &&
      form.endDate < form.startDate
    ) {
      setMsg(
        "❌ End date must be after start date."
      );

      return;
    }

    if (
      form.recurrenceType === "custom" &&
      form.customDays.length === 0
    ) {
      setMsg(
        "❌ Select at least one day for custom recurrence."
      );

      return;
    }

    setSaving(true);

    setMsg("");

    try {
      const durationMins = getDurationMins(form.time, form.endTime);
      const base = {
        title: form.title,

        agenda: form.agenda || "",

        scheduledBy: userProfile.uid,

        scheduledByName:
          userProfile.displayName || "Admin",

        time: form.time,
        startTime: form.time,
        endTime: form.endTime,
        durationMinutes: durationMins,
        duration: durationMins,

        meetLink: form.meetLink || "",

        audience: form.audience || "all",

        status: "upcoming",

        recurrenceType: form.recurrenceType,

        createdAt: serverTimestamp(),

        updatedAt: serverTimestamp(),
      };

      // One-time meeting
      if (form.recurrenceType === "once") {
        await addDoc(collection(db, "meetings"), {
          ...base,

          date: form.startDate,
        });

       await addNotification({
    type: "meeting",
    title: "New Meeting Scheduled",
    message: `${userProfile.displayName || "Admin"} scheduled a meeting: ${form.title}`,
    recipientId: form.audience === "all" ? "all" : form.audience,
    
});

        setMsg("✅ Meeting scheduled!");
      } else {
        // Recurring meetings
        const dates = getDatesBetween(
          form.startDate,
          form.endDate,
          getAllowedDays(
            form.recurrenceType,
            form.customDays
          )
        );

        if (dates.length === 0) {
          setMsg(
            "❌ No valid days found in that date range."
          );

          setSaving(false);

          return;
        }

        const batch = writeBatch(db);

        dates.forEach((date) => {
          const ref = doc(
            collection(db, "meetings")
          );

          batch.set(ref, {
            ...base,
            date,
          });
        });

        await batch.commit();

        await addNotification({
            type: "info",
            title: "New Recurring Meeting Scheduled",
            message: `${userProfile.displayName || "Admin"} scheduled a recurring meeting: ${form.title}`,
            recipientId: form.audience === "all" ? "all" : form.audience,
            link: "/employee/dashboard"
        });

        setMsg(
          `✅ ${dates.length} meetings scheduled!`
        );
      }

      // Reset form
      setForm({
        title: "",
        startDate: "",
        endDate: "",
        time: "",
        endTime: "",
        meetLink: "",
        audience: "all",
        agenda: "",
        recurrenceType: "once",
        customDays: [],
      });
      setHasManuallyEditedEndTime(false);
    } catch (err) {
      setMsg("❌ " + err.message);
    }

    setSaving(false);
  }

  function openEdit(m) {
    setEditItem(m);
    setEditForm({
      title: m.title || "",
      agenda: m.agenda || "",
      date: m.date || "",
      time: m.time || m.startTime || "",
      endTime: m.endTime || "",
      meetLink: m.meetLink || "",
      audience: m.audience || "all",
    });
    setEditMsg("");
  }

  async function handleEditSave() {
    setEditSaving(true);
    setEditMsg("");
    try {
      const durationMins = getDurationMins(editForm.time, editForm.endTime);
      await updateDoc(doc(db, "meetings", editItem.id), {
        title: editForm.title,
        agenda: editForm.agenda,
        date: editForm.date,
        time: editForm.time,
        startTime: editForm.time,
        endTime: editForm.endTime,
        durationMinutes: durationMins,
        duration: durationMins,
        meetLink: editForm.meetLink,
        audience: editForm.audience,
        updatedAt: serverTimestamp(),
      });
      setEditMsg("✅ Updated!");
      setTimeout(() => {
        setEditItem(null);
        setEditMsg("");
      }, 900);
    } catch (err) {
      setEditMsg("❌ " + err.message);
    }
    setEditSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this meeting?")) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await deleteDoc(doc(db, "meetings", id));
    } catch (err) {
      console.error(err);
    }
    setDeleting((d) => ({ ...d, [id]: false }));
  }

  // ─────────────────────────────────────────────────────────
  // Join Meeting
  // ─────────────────────────────────────────────────────────
  async function handleJoin(meeting) {
    if (!userProfile?.uid) return;

    setJoining((p) => ({
      ...p,
      [meeting.id]: true,
    }));

    try {
      await joinMeeting(
        meeting.id,
        userProfile.uid
      );

      if (meeting.meetLink) {
        window.open(
          meeting.meetLink,
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (err) {
      console.error("[AdminMeetings] join error:", err);
    }

    setJoining((p) => ({
      ...p,
      [meeting.id]: false,
    }));
  }

  // ─────────────────────────────────────────────────────────
  // Leave Meeting
  // ─────────────────────────────────────────────────────────
  async function handleLeave() {
    if (!userProfile?.uid) return;

    await leaveMeeting(userProfile.uid);
  }

  const isInThisMeeting = (id) =>
    userProfile?.inMeeting &&
    userProfile?.currentMeetingId === id;

  // Categories
  const todayActive = getTodaysMeetings(allTodayMeetings, userProfile);
  const completedToday = (allTodayMeetings || []).filter(
    (m) => isEmployeeVisible(m, userProfile) && getMeetingState(m, userProfile) === "COMPLETED"
  );
  const missedToday = (allTodayMeetings || []).filter(
    (m) => isEmployeeVisible(m, userProfile) && getMeetingState(m, userProfile) === "MISSED"
  );

  const displayedMeetings =
    listTab === "today"
      ? todayActive
      : listTab === "completed"
      ? completedToday
      : missedToday;

  // ─────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────
  return (
    <Layout
      navItems={ADMIN_NAV}
      panelLabel="Admin Panel"
    >
      <div className="page-header">
        <h1 className="page-title">
          Meetings
        </h1>
      </div>

      <div className="two-col-layout">
        {/* Schedule Form */}
        <div className="form-card">
          <h3 className="form-card-title">
            Schedule Meeting
          </h3>

          {msg && (
            <div
              className={
                msg.startsWith("✅")
                  ? "form-success"
                  : "form-error"
              }
            >
              {msg}
            </div>
          )}

          <form onSubmit={handleSchedule}>
            <div className="form-group">
              <label>Meeting Title *</label>

              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="Sprint Review"
              />
            </div>

            {/* Recurrence */}
            <div className="form-group">
              <label>Recurrence *</label>

              <div className="recurrence-tabs">
                {[
                  {
                    value: "once",
                    label: "One-time",
                  },

                  {
                    value: "mon-fri",
                    label: "Mon – Fri",
                  },

                  {
                    value: "mon-sat",
                    label: "Mon – Sat",
                  },

                  {
                    value: "custom",
                    label: "Custom days",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`recurrence-tab ${
                      form.recurrenceType ===
                      opt.value
                        ? "recurrence-tab--active"
                        : ""
                    }`}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        recurrenceType:
                          opt.value,
                        customDays: [],
                      }))
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Days */}
            {form.recurrenceType === "custom" && (
              <div className="form-group">
                <label>Select Days *</label>

                <div className="day-picker">
                  {DAY_LABELS.map(
                    (label, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`day-btn ${
                          form.customDays.includes(
                            idx
                          )
                            ? "day-btn--active"
                            : ""
                        }`}
                        onClick={() =>
                          toggleCustomDay(idx)
                        }
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Dates */}
            {form.recurrenceType === "once" ? (
              <div className="form-group">
                <label>Date *</label>

                <input
                  name="startDate"
                  type="date"
                  value={form.startDate}
                  onChange={handleChange}
                  min={today}
                  required
                />
              </div>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Start Date *
                  </label>

                  <input
                    name="startDate"
                    type="date"
                    value={form.startDate}
                    onChange={handleChange}
                    min={today}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>

                  <input
                    name="endDate"
                    type="date"
                    value={form.endDate}
                    onChange={handleChange}
                    min={
                      form.startDate ||
                      today
                    }
                    required
                  />
                </div>
              </div>
            )}

            {/* Preview */}
            {form.recurrenceType !== "once" &&
              previewCount > 0 && (
                <div
  className="recurrence-preview"
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="16"
    height="16"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
    />
  </svg>

  <span>
    Will create <strong>{previewCount}</strong> meeting
    {previewCount !== 1 ? "s" : ""}
  </span>
</div>
              )}

            {/* Time */}
            <div className="form-row">
              <div className="form-group">
                <label>Start Time *</label>

                <input
                  name="time"
                  type="time"
                  value={form.time}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    setForm((p) => {
                      const updated = { ...p, time: newTime };
                      if (!hasManuallyEditedEndTime) {
                        updated.endTime = add30Minutes(newTime);
                      }
                      return updated;
                    });
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label>End Time *</label>

                <input
                  name="endTime"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => {
                    setHasManuallyEditedEndTime(true);
                    handleChange(e);
                  }}
                  required
                />
              </div>
            </div>

            {/* Link */}
            <div className="form-group">
              <label>Meeting Link *</label>

              <input
                name="meetLink"
                value={form.meetLink}
                onChange={handleChange}
                placeholder="https://meet.google.com/..."
                required
              />
            </div>

            {/* Audience */}
            <div className="form-group">
              <label>Audience</label>

              <select
                name="audience"
                value={form.audience}
                onChange={handleChange}
              >
                <option value="all">
                  All Employees
                </option>

                <option value="leads">
                  Team Leads Only
                </option>

                {activeDepts.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Agenda */}
            <div className="form-group">
              <label>Agenda *</label>

              <textarea
                name="agenda"
                value={form.agenda}
                onChange={handleChange}
                placeholder="Meeting agenda..."
                rows={3}
                required
              />
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving
                ? "Scheduling..."
                : "Schedule Meeting"}
            </button>
          </form>
        </div>

        {/* Meetings List */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">
              Meetings
            </h3>

            <div className="meetings-tab-toggle" style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className={`meetings-tab-btn ${
                  listTab === "today" ? "meetings-tab-btn--active" : ""
                }`}
                onClick={() => setListTab("today")}
              >
                Today's Meetings ({todayActive.length})
              </button>

              <button
                className={`meetings-tab-btn ${
                  listTab === "completed" ? "meetings-tab-btn--active" : ""
                }`}
                onClick={() => setListTab("completed")}
              >
                Completed ({completedToday.length})
              </button>

              <button
                className={`meetings-tab-btn ${
                  listTab === "missed" ? "meetings-tab-btn--active" : ""
                }`}
                onClick={() => setListTab("missed")}
              >
                Missed ({missedToday.length})
              </button>
            </div>
          </div>

          {todayMeetingsLoading ? (
            <div className="card-loading">
              Loading...
            </div>
          ) : displayedMeetings.length === 0 ? (
            <div className="card-empty">
              {listTab === "today"
                ? "No active meetings scheduled for today."
                : listTab === "completed"
                ? "No completed meetings today."
                : "No missed meetings today."}
            </div>
          ) : (
            <div className="meetings-full-list">
              {displayedMeetings.map((m) => {
                const state = getMeetingState(m, userProfile);
                const inThis = isInThisMeeting(m.id);

                return (
                  <div
                    key={m.id}
                    className={[
                      "meeting-card",
                      inThis ? "meeting-card--active" : "",
                      (state === "COMPLETED" || state === "MISSED") ? "meeting-card--expired" : "",
                    ].join(" ")}
                  >
                    <div className="meeting-card-header">
                      <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }}
>
  <span className="meeting-card-title">
    {m.title}
  </span>

  {isInThisMeeting(m.id) && (
    <span
      style={{
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "#22c55e",
        boxShadow: "0 0 8px #22c55e",
        animation: "meetingPulse 1.5s infinite",
      }}
    />
  )}
</div>

                      <div
                        style={{
                          display: "flex",
                          gap: "0.4rem",
                          alignItems:
                            "center",
                        }}
                      >
                        {state === "COMPLETED" ? (
                          <span className="status-pill status-pill--gray">
                            Completed
                          </span>
                        ) : state === "MISSED" ? (
                          <span className="status-pill status-pill--red" style={{ backgroundColor: "#ef4444", color: "#fff" }}>
                            Missed
                          </span>
                        ) : state === "LIVE" ? (
                          <span className="status-pill status-pill--green">
                            Live
                          </span>
                        ) : (
                          <span className="status-pill status-pill--orange" style={{ backgroundColor: "#f97316", color: "#fff" }}>
                            Upcoming
                          </span>
                        )}

                        <span className="status-pill status-pill--blue">
                          {m.audience}
                        </span>

                        {m.scheduledBy === userProfile?.uid && (
                          <>
                            <button
                              className="ann-icon-btn"
                              title="Edit"
                              onClick={() => openEdit(m)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                              </svg>
                            </button>
                            <button
                              className="ann-icon-btn ann-icon-btn--danger"
                              title="Delete"
                              onClick={() => handleDelete(m.id)}
                              disabled={deleting[m.id]}
                            >
                              {deleting[m.id] ? "…" : (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                </svg>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="meeting-card-meta">
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          width="14"
                          height="14"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                          />
                        </svg>

                        {m.date}

                        <span>·</span>

                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          width="14"
                          height="14"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>

                        {formatTimeStr(m.startTime || m.time)}
                        {m.endTime && ` - ${formatTimeStr(m.endTime)}`}
                        {(m.durationMinutes || m.duration) ? ` (${m.durationMinutes || m.duration} mins)` : ""}
                      </span>

                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          width="14"
                          height="14"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                          />
                        </svg>

                        {m.scheduledByName}
                      </span>
                    </div>

                    {m.agenda && (
                      <p className="meeting-card-agenda">
                        {m.agenda}
                      </p>
                    )}

                    <div className="meeting-card-actions">
                      {(state === "COMPLETED" || state === "MISSED") ? (
                        <span className="meeting-link-expired">
                          🔒 Meeting link expired
                        </span>
                      ) : inThis ? (
                        <div className="meeting-active-row">
                          <span className="meeting-active-indicator">
                            <span className="meeting-active-pulse" />
                            You're in this meeting
                          </span>

                          <button
                            className="btn-leave-meeting"
                            onClick={handleLeave}
                          >
                            Leave Meeting
                          </button>
                        </div>
                      ) : (
                        m.meetLink && (
                          <button
                            className="btn-join-meeting"
                            onClick={() => handleJoin(m)}
                            disabled={
                              joining[m.id] ||
                              userProfile?.inMeeting ||
                              state !== "LIVE"
                            }
                            title={
                              state !== "LIVE"
                                ? "Meeting link activates 10 minutes before start time"
                                : userProfile?.inMeeting && !inThis
                                ? "You're already in another meeting"
                                : ""
                            }
                          >
                            {joining[m.id]
                              ? "Joining…"
                              : state === "LIVE"
                              ? "Join Meeting →"
                              : "Available 10 mins before"}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editItem && editForm && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Meeting</h2>
              <button className="modal-close" onClick={() => setEditItem(null)}>✕</button>
            </div>
            {editMsg && (
              <div className={editMsg.startsWith("✅") ? "form-success" : "form-error"}>
                {editMsg}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" className="form-input" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Audience</label>
                <select className="form-select" value={editForm.audience} onChange={(e) => setEditForm({ ...editForm, audience: e.target.value })}>
                  <option value="all">All Employees</option>
                  <option value="leads">Team Leads Only</option>
                  {activeDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Time *</label>
                <input type="time" className="form-input" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Time *</label>
                <input type="time" className="form-input" value={editForm.endTime} onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Meeting Link</label>
              <input className="form-input" value={editForm.meetLink} onChange={(e) => setEditForm({ ...editForm, meetLink: e.target.value })} placeholder="https://meet.google.com/..." />
            </div>
            <div className="form-group">
              <label className="form-label">Agenda</label>
              <textarea className="form-input" rows={3} value={editForm.agenda} onChange={(e) => setEditForm({ ...editForm, agenda: e.target.value })} placeholder="Meeting agenda..." />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave} disabled={editSaving}>
                {editSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default AdminMeetings;